import React, { useState, useEffect } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Zap,
  Table,
  ChevronRight,
  ChevronDown,
  Database,
  Play,
  Truck,
  Target,
  Navigation,
  Code,
  Boxes,
  FileText,
  Info,
  X
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { biTableStatusCollection } from '../services/biRecords';

export default function PowerBIBetaWorkflow({ tenantId } = {}) {
  const [uploadMode, setUploadMode] = useState('system'); // 'manual' or 'system'
  const [syncStatus, setSyncStatus] = useState(null);
  const [transformStatus, setTransformStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingFile, setSyncingFile] = useState(null);
  const [syncProgress, setSyncProgress] = useState({}); // key → 'pending'|'syncing'|'success'|'error'|'skipped'

  const POWERBI_KEYS = ['sod_openpick', 'sod_invoices_curr', 'sod_invoice_hst', 'ra_qoh', 'openwipdaily', 'rcvdwipdaily', 'pod_matson'];

  const loadSyncStatus = async () => {
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'getAzureSyncStatus')();
      setSyncStatus(result.data?.files?.filter(f => f.workflow === 'powerbi') || []);
      setTransformStatus(result.data?.transform || null);
    } catch (e) { console.warn('Could not load sync status:', e.message); }
  };

  useEffect(() => { loadSyncStatus(); }, []);

  const triggerSync = async (force = false) => {
    setSyncing(true);
    const progress = {};
    POWERBI_KEYS.forEach(k => { progress[k] = 'pending'; });
    setSyncProgress(progress);

    const functions = getFunctions();
    const syncFn = httpsCallable(functions, 'syncAzureData', { timeout: 570000 });

    for (const key of POWERBI_KEYS) {
      setSyncingFile(key);
      setSyncProgress(prev => ({ ...prev, [key]: 'syncing' }));
      try {
        const result = await syncFn({ files: [key], force });
        const fileResult = result.data?.results?.[0];
        setSyncProgress(prev => ({ ...prev, [key]: fileResult?.status || 'success' }));
      } catch (e) {
        console.error(`Sync failed for ${key}:`, e.message);
        setSyncProgress(prev => ({ ...prev, [key]: 'error' }));
      }
    }

    setSyncingFile(null);
    setSyncing(false);
    await loadSyncStatus();
  };

  const syncSingleFile = async (key) => {
    setSyncing(true);
    setSyncingFile(key);
    setSyncProgress({ [key]: 'syncing' });
    try {
      const functions = getFunctions();
      const result = await httpsCallable(functions, 'syncAzureData', { timeout: 570000 })({ files: [key], force: true });
      const fileResult = result.data?.results?.[0];
      setSyncProgress({ [key]: fileResult?.status || 'success' });
    } catch (e) {
      console.error(`Sync failed for ${key}:`, e.message);
      setSyncProgress({ [key]: 'error' });
    }
    setSyncingFile(null);
    setSyncing(false);
    await loadSyncStatus();
  };

  const [currentStep, setCurrentStep] = useState(1);

  // Orders - Pick & Open CSV upload state (Step 1)
  const [rabiFile, setRabiFile] = useState(null);
  const [rabiUploading, setRabiUploading] = useState(false);
  const [rabiProgress, setRabiProgress] = useState(0);
  const [rabiResult, setRabiResult] = useState(null);
  const [rabiError, setRabiError] = useState(null);

  // Orders - Current Invoices CSV upload state (Step 1.1)
  const [invocFile, setInvocFile] = useState(null);
  const [invocUploading, setInvocUploading] = useState(false);
  const [invocProgress, setInvocProgress] = useState(0);
  const [invocResult, setInvocResult] = useState(null);
  const [invocError, setInvocError] = useState(null);

  // Orders - Historical Invoices CSV upload state (Step 1.2)
  const [histFile, setHistFile] = useState(null);
  const [histUploading, setHistUploading] = useState(false);
  const [histProgress, setHistProgress] = useState(0);
  const [histResult, setHistResult] = useState(null);
  const [histError, setHistError] = useState(null);

  // Production Errors CSV upload state (Step 2a)
  const [errorsFile, setErrorsFile] = useState(null);
  const [errorsUploading, setErrorsUploading] = useState(false);
  const [errorsProgress, setErrorsProgress] = useState(0);
  const [errorsResult, setErrorsResult] = useState(null);
  const [errorsError, setErrorsError] = useState(null);

  // Production OpenWIP CSV upload state (Step 2b)
  const [wipFile, setWipFile] = useState(null);
  const [wipUploading, setWipUploading] = useState(false);
  const [wipProgress, setWipProgress] = useState(0);
  const [wipResult, setWipResult] = useState(null);
  const [wipError, setWipError] = useState(null);

  // Production 2025 Receipts CSV upload state (Step 2c)
  const [rcptsFile, setRcptsFile] = useState(null);
  const [rcptsUploading, setRcptsUploading] = useState(false);
  const [rcptsProgress, setRcptsProgress] = useState(0);
  const [rcptsResult, setRcptsResult] = useState(null);
  const [rcptsError, setRcptsError] = useState(null);

  // Production 2025 Receipts HISTORICAL CSV upload state (Step 2d) — CHA-326
  const [rcptsHistFile, setRcptsHistFile] = useState(null);
  const [rcptsHistUploading, setRcptsHistUploading] = useState(false);
  const [rcptsHistProgress, setRcptsHistProgress] = useState(0);
  const [rcptsHistResult, setRcptsHistResult] = useState(null);
  const [rcptsHistError, setRcptsHistError] = useState(null);

  // Inventory upload state (Step 2.1)
  const [invFile, setInvFile] = useState(null);
  const [invUploading, setInvUploading] = useState(false);
  const [invProgress, setInvProgress] = useState(0);
  const [invResult, setInvResult] = useState(null);
  const [invError, setInvError] = useState(null);

  // HTS upload state (Step 2.2)
  const [htsFile, setHtsFile] = useState(null);
  const [htsUploading, setHtsUploading] = useState(false);
  const [htsProgress, setHtsProgress] = useState(0);
  const [htsResult, setHtsResult] = useState(null);
  const [htsError, setHtsError] = useState(null);

  // CHA-88 — upload slots for openpt + walmart_tms + target
  // Open PT (openpt_csv) upload state
  const [openptFile, setOpenptFile] = useState(null);
  const [openptUploading, setOpenptUploading] = useState(false);
  const [openptProgress, setOpenptProgress] = useState(0);
  const [openptResult, setOpenptResult] = useState(null);
  const [openptError, setOpenptError] = useState(null);

  // Walmart TMS BWI (walmart_tms_bwi_csv) upload state
  const [walmartBwiFile, setWalmartBwiFile] = useState(null);
  const [walmartBwiUploading, setWalmartBwiUploading] = useState(false);
  const [walmartBwiProgress, setWalmartBwiProgress] = useState(0);
  const [walmartBwiResult, setWalmartBwiResult] = useState(null);
  const [walmartBwiError, setWalmartBwiError] = useState(null);

  // Walmart TMS DSDC (walmart_tms_dsdc_csv) upload state
  const [walmartDsdcFile, setWalmartDsdcFile] = useState(null);
  const [walmartDsdcUploading, setWalmartDsdcUploading] = useState(false);
  const [walmartDsdcProgress, setWalmartDsdcProgress] = useState(0);
  const [walmartDsdcResult, setWalmartDsdcResult] = useState(null);
  const [walmartDsdcError, setWalmartDsdcError] = useState(null);

  // Walmart TMS IMG (walmart_tms_img_csv) upload state
  const [walmartImgFile, setWalmartImgFile] = useState(null);
  const [walmartImgUploading, setWalmartImgUploading] = useState(false);
  const [walmartImgProgress, setWalmartImgProgress] = useState(0);
  const [walmartImgResult, setWalmartImgResult] = useState(null);
  const [walmartImgError, setWalmartImgError] = useState(null);

  // Target TMS upload state (targetUpload* prefix avoids collision with existing targetStatus/targetError status-check state)
  const [targetUploadFile, setTargetUploadFile] = useState(null);
  const [targetUploadUploading, setTargetUploadUploading] = useState(false);
  const [targetUploadProgress, setTargetUploadProgress] = useState(0);
  const [targetUploadResult, setTargetUploadResult] = useState(null);
  const [targetUploadError, setTargetUploadError] = useState(null);

  // BL sync state
  const [blSyncing, setBlSyncing] = useState(false);
  const [blResult, setBlResult] = useState(null);
  const [blError, setBlError] = useState(null);
  const [blConfig, setBlConfig] = useState(null);

  // LAP sync state
  const [lapSyncing, setLapSyncing] = useState(false);
  const [lapResult, setLapResult] = useState(null);
  const [lapError, setLapError] = useState(null);
  const [lapConfig, setLapConfig] = useState(null);

  // Container Log sync state
  const [containerSyncing, setContainerSyncing] = useState(false);
  const [containerResult, setContainerResult] = useState(null);
  const [containerError, setContainerError] = useState(null);
  const [containerConfig, setContainerConfig] = useState(null);

  // Walmart status state
  const [walmartLoading, setWalmartLoading] = useState(false);
  const [walmartStatus, setWalmartStatus] = useState(null);
  const [walmartError, setWalmartError] = useState(null);

  // Target status state
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [targetError, setTargetError] = useState(null);

  // Transform state
  const [transforming, setTransforming] = useState(false);
  const [transformResult, setTransformResult] = useState(null);
  const [transformError, setTransformError] = useState(null);
  const [showSQL, setShowSQL] = useState(false);

  // Run All (Steps 3-7) state
  const [runningAll, setRunningAll] = useState(false);
  const [runAllStep, setRunAllStep] = useState(null);
  const [runAllError, setRunAllError] = useState(null);

  // Table info popup state
  const [tableStatuses, setTableStatuses] = useState({});
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);

  const db = getFirestore();

  useEffect(() => {
    loadRoutingConfigs();
  }, []);

  // CHA-616 — statuses are per tenant, so reload when the tenant is known or changes.
  useEffect(() => {
    // CHA-616 — status is tenant data: drop the previous tenant's statuses immediately, and let only a
    // response for the STILL-CURRENT tenant commit (a slow tenant-A read must not land after tenant B).
    setTableStatuses({});
    if (!tenantId) return undefined;
    let current = true;
    loadTableStatuses(tenantId, () => current);
    return () => { current = false; };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTableStatuses = async (forTenantId, isCurrent) => {
    try {
      // CHA-616 — the tenant's own status records (CHA-87 E2a), not the shared root collection.
      const statusRef = biTableStatusCollection(db, forTenantId);
      const snapshot = await getDocs(statusRef);
      if (!isCurrent()) return; // the tenant changed while this read was in flight
      const statuses = {};
      snapshot.forEach(doc => {
        statuses[doc.id] = { id: doc.id, ...doc.data() };
      });
      setTableStatuses(statuses);
    } catch (error) {
      console.error('Error loading table statuses:', error);
    }
  };

  const loadRoutingConfigs = async () => {
    try {
      const configsRef = collection(db, 'routing_sync_configs');
      const snapshot = await getDocs(configsRef);

      snapshot.forEach(doc => {
        const config = { id: doc.id, ...doc.data() };
        if (config.tableName === 'bl' && config.enabled) {
          setBlConfig(config);
        } else if (config.tableName === 'lap' && config.enabled) {
          setLapConfig(config);
        } else if (config.tableName === 'container_log' && config.enabled) {
          setContainerConfig(config);
        }
      });
    } catch (error) {
      console.error('Error loading routing configs:', error);
    }
  };

  // Handle file selections
  const handleRabiFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setRabiFile(file); setRabiResult(null); setRabiError(null); }
  };
  const handleInvocFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setInvocFile(file); setInvocResult(null); setInvocError(null); }
  };
  const handleHistFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setHistFile(file); setHistResult(null); setHistError(null); }
  };
  const handleErrorsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setErrorsFile(file); setErrorsResult(null); setErrorsError(null); }
  };
  const handleWipFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setWipFile(file); setWipResult(null); setWipError(null); }
  };
  const handleRcptsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setRcptsFile(file); setRcptsResult(null); setRcptsError(null); }
  };
  const handleRcptsHistFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setRcptsHistFile(file); setRcptsHistResult(null); setRcptsHistError(null); }
  };
  const handleInvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setInvFile(file); setInvResult(null); setInvError(null); }
  };
  const handleHtsFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setHtsFile(file); setHtsResult(null); setHtsError(null); }
  };

  // 🆕 CHA-88 Phase A — file-select handlers for parallel-write slots
  const handleOpenptFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setOpenptFile(file); setOpenptResult(null); setOpenptError(null); }
  };
  const handleWalmartBwiFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setWalmartBwiFile(file); setWalmartBwiResult(null); setWalmartBwiError(null); }
  };
  const handleWalmartDsdcFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setWalmartDsdcFile(file); setWalmartDsdcResult(null); setWalmartDsdcError(null); }
  };
  const handleWalmartImgFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setWalmartImgFile(file); setWalmartImgResult(null); setWalmartImgError(null); }
  };
  const handleTargetUploadFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setTargetUploadFile(file); setTargetUploadResult(null); setTargetUploadError(null); }
  };

  // Generic CSV upload via processBIUpload
  const uploadCSVFile = async (file, storagePath, tableName, setUploading, setProgress, setResult, setError, nextStep) => {
    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const storage = getStorage();
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: tableName,
        datasetName: 'inmocean_raw',
        tenantId
      });

      setProgress(100);
      setResult({
        success: true,
        filename: file.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setUploading(false);
      if (nextStep !== undefined) setCurrentStep(nextStep);
      return result;
    } catch (err) {
      setError(err.message);
      setUploading(false);
      throw err;
    }
  };

  // Upload Orders - Pick & Open CSV (Step 1) — REPLACE
  const uploadRabiInvoice = async () => {
    if (!rabiFile) return;

    setRabiUploading(true);
    setRabiProgress(0);
    setRabiError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow-beta/rabi_invoices_v2_po.csv`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, rabiFile);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setRabiProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setRabiProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setRabiProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'rabi_invoices_v2_csv_po',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform dates
      setRabiProgress(80);
      const transformRabiInvoicesTable = httpsCallable(functions, 'transformRabiInvoicesTable', { timeout: 330000 });
      await transformRabiInvoicesTable({
        sourceTable: 'rabi_invoices_v2_csv_po',
        targetTable: 'rabi_invoices_v2_csv_po',
        dataset: 'inmocean_raw'
      });

      setRabiProgress(100);
      setRabiResult({
        success: true,
        filename: rabiFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setRabiUploading(false);
    } catch (err) {
      setRabiError(err.message);
      setRabiUploading(false);
    }
  };

  // Upload Orders - Current Invoices CSV (Step 1.1) — REPLACE
  const uploadInvoices = async () => {
    if (!invocFile) return;

    setInvocUploading(true);
    setInvocProgress(0);
    setInvocError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow-beta/rabi_invoices_v2_inv.csv`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, invocFile);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setInvocProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setInvocProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setInvocProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'rabi_invoices_v2_csv_inv',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform dates
      setInvocProgress(80);
      const transformRabiInvoicesTable = httpsCallable(functions, 'transformRabiInvoicesTable', { timeout: 330000 });
      await transformRabiInvoicesTable({
        sourceTable: 'rabi_invoices_v2_csv_inv',
        targetTable: 'rabi_invoices_v2_csv_inv',
        dataset: 'inmocean_raw'
      });

      setInvocProgress(100);
      setInvocResult({
        success: true,
        filename: invocFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setInvocUploading(false);
    } catch (err) {
      setInvocError(err.message);
      setInvocUploading(false);
    }
  };

  // Upload Orders - Historical Invoices CSV (Step 1.2) — REPLACE
  const uploadHistorical = async () => {
    if (!histFile) return;

    setHistUploading(true);
    setHistProgress(0);
    setHistError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow-beta/rabi_invoices_v2_inv_hist.csv`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, histFile);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setHistProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setHistProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setHistProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'rabi_invoices_v2_csv_inv_hist',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform dates
      setHistProgress(80);
      const transformRabiInvoicesTable = httpsCallable(functions, 'transformRabiInvoicesTable', { timeout: 330000 });
      await transformRabiInvoicesTable({
        sourceTable: 'rabi_invoices_v2_csv_inv_hist',
        targetTable: 'rabi_invoices_v2_csv_inv_hist',
        dataset: 'inmocean_raw'
      });

      setHistProgress(100);
      setHistResult({
        success: true,
        filename: histFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setHistUploading(false);
      setCurrentStep(2);
    } catch (err) {
      setHistError(err.message);
      setHistUploading(false);
    }
  };

  // Upload Production Errors CSV (Step 2a)
  const uploadErrorsData = async () => {
    if (!errorsFile) return;
    try {
      await uploadCSVFile(
        errorsFile,
        'inmocean-raw-data/powerbi-workflow-beta/production_errors.csv',
        'production_errors_csv',
        setErrorsUploading, setErrorsProgress, setErrorsResult, setErrorsError
      );
    } catch (err) { /* handled by uploadCSVFile */ }
  };

  // Upload Production OpenWIP CSV (Step 2b)
  const uploadWipData = async () => {
    if (!wipFile) return;
    try {
      await uploadCSVFile(
        wipFile,
        'inmocean-raw-data/powerbi-workflow-beta/production_open_wip.csv',
        'production_open_wip_csv',
        setWipUploading, setWipProgress, setWipResult, setWipError
      );
    } catch (err) { /* handled by uploadCSVFile */ }
  };

  // Upload Production 2025 Receipts CSV (Step 2c)
  const uploadRcptsData = async () => {
    if (!rcptsFile) return;
    try {
      await uploadCSVFile(
        rcptsFile,
        'inmocean-raw-data/powerbi-workflow-beta/production_2025_receipts.csv',
        'production_2025_receipts_csv',
        setRcptsUploading, setRcptsProgress, setRcptsResult, setRcptsError
      );
    } catch (err) { /* handled by uploadCSVFile */ }
  };

  // Upload Production 2025 Receipts HISTORICAL CSV (Step 2d) — CHA-326, REPLACE.
  // Lands in production_2025_receipts_csv_hist; the Order File transform unions it with the current
  // receipts feed (current-wins on pod_pkey) so the received table carries full history + current.
  const uploadRcptsHistData = async () => {
    if (!rcptsHistFile) return;
    try {
      await uploadCSVFile(
        rcptsHistFile,
        'inmocean-raw-data/powerbi-workflow-beta/production_2025_receipts_hist.csv',
        'production_2025_receipts_csv_hist',
        setRcptsHistUploading, setRcptsHistProgress, setRcptsHistResult, setRcptsHistError
      );
    } catch (err) { /* handled by uploadCSVFile */ }
  };

  // Upload Inventory CSV (Step 2.1)
  const uploadInventoryData = async () => {
    if (!invFile) return;

    setInvUploading(true);
    setInvProgress(0);
    setInvError(null);

    try {
      const storage = getStorage();
      const storagePath = `inmocean-raw-data/powerbi-workflow-beta/inventory.csv`;
      const storageRef = ref(storage, storagePath);

      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, invFile);
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 50);
            setInvProgress(progress);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      setInvProgress(55);
      const functions = getFunctions();
      const processBIUpload = httpsCallable(functions, 'processBIUpload', { timeout: 600000 });

      setInvProgress(60);
      const result = await processBIUpload({
        bucketName: 'actualizer2.firebasestorage.app',
        filePath: storagePath,
        tableName: 'inventory_csv',
        datasetName: 'inmocean_raw',
        tenantId
      });

      // Transform inventory table
      setInvProgress(70);
      const transformInventoryTable = httpsCallable(functions, 'transformInventoryTable', { timeout: 330000 });
      await transformInventoryTable({
        sourceTable: 'inventory_csv',
        targetTable: 'inventory_csv',
        dataset: 'inmocean_raw'
      });
      console.log('✅ inventory_csv table transformed');

      // Create error_ench table after inventory upload (matches XLS workflow)
      setInvProgress(90);
      const createErrorEnch = httpsCallable(functions, 'createErrorEnchTable', { timeout: 150000 });
      try {
        await createErrorEnch();
        console.log('✅ error_ench table created');
      } catch (errEnch) {
        console.warn('⚠️ Could not create error_ench table:', errEnch.message);
      }

      setInvProgress(100);
      setInvResult({
        success: true,
        filename: invFile.name,
        rowCount: result.data?.rowsInserted || result.data?.rowCount || 0
      });
      setInvUploading(false);
      setCurrentStep(2.2);
    } catch (err) {
      setInvError(err.message);
      setInvUploading(false);
    }
  };

  // Upload HTS CSV (Step 2.2)
  const uploadHtsData = async () => {
    if (!htsFile) return;
    try {
      await uploadCSVFile(
        htsFile,
        'inmocean-raw-data/powerbi-workflow-beta/hts_updates.csv',
        'hts_updates_csv',
        setHtsUploading, setHtsProgress, setHtsResult, setHtsError,
        3
      );
    } catch (err) { /* handled by uploadCSVFile */ }
  };

  // 🆕 CHA-88 Phase A — upload handlers for parallel-write slots
  // These five upload to actualizer2.inmocean_raw.*_csv tables (no transform chain).
  // No transform chain — direct loads via the shared uploadCSVFile helper.
  const uploadOpenpt = async () => {
    if (!openptFile) return;
    try {
      await uploadCSVFile(
        openptFile,
        'inmocean-raw-data/powerbi-workflow-beta/openpt.csv',
        'openpt_csv',
        setOpenptUploading, setOpenptProgress, setOpenptResult, setOpenptError
      );
    } catch (err) {
      console.warn('[CHA-88] upload handler caught error (already reported by uploadCSVFile):', err);
    }
  };

  const uploadWalmartBwi = async () => {
    if (!walmartBwiFile) return;
    try {
      await uploadCSVFile(
        walmartBwiFile,
        'inmocean-raw-data/powerbi-workflow-beta/walmart_tms_bwi.csv',
        'walmart_tms_bwi_csv',
        setWalmartBwiUploading, setWalmartBwiProgress, setWalmartBwiResult, setWalmartBwiError
      );
    } catch (err) {
      console.warn('[CHA-88] upload handler caught error (already reported by uploadCSVFile):', err);
    }
  };

  const uploadWalmartDsdc = async () => {
    if (!walmartDsdcFile) return;
    try {
      await uploadCSVFile(
        walmartDsdcFile,
        'inmocean-raw-data/powerbi-workflow-beta/walmart_tms_dsdc.csv',
        'walmart_tms_dsdc_csv',
        setWalmartDsdcUploading, setWalmartDsdcProgress, setWalmartDsdcResult, setWalmartDsdcError
      );
    } catch (err) {
      console.warn('[CHA-88] upload handler caught error (already reported by uploadCSVFile):', err);
    }
  };

  const uploadWalmartImg = async () => {
    if (!walmartImgFile) return;
    try {
      await uploadCSVFile(
        walmartImgFile,
        'inmocean-raw-data/powerbi-workflow-beta/walmart_tms_img.csv',
        'walmart_tms_img_csv',
        setWalmartImgUploading, setWalmartImgProgress, setWalmartImgResult, setWalmartImgError
      );
    } catch (err) {
      console.warn('[CHA-88] upload handler caught error (already reported by uploadCSVFile):', err);
    }
  };

  const uploadTargetCsv = async () => {
    if (!targetUploadFile) return;
    try {
      await uploadCSVFile(
        targetUploadFile,
        'inmocean-raw-data/powerbi-workflow-beta/target.csv',
        'target_csv',
        setTargetUploadUploading, setTargetUploadProgress, setTargetUploadResult, setTargetUploadError
      );
    } catch (err) {
      console.warn('[CHA-88] upload handler caught error (already reported by uploadCSVFile):', err);
    }
  };

  // Steps 3-7 are identical to the original workflow
  const syncBL = async () => {
    if (!blConfig) { setBlError('BL sync not configured. Contact admin.'); return; }
    setBlSyncing(true); setBlError(null);
    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
      const result = await syncFunction({
        sheetId: blConfig.sheetId, tabName: blConfig.tabName,
        tableName: blConfig.tableName, configId: blConfig.id
      });
      if (result.data?.success) {
        setBlResult({ success: true, rowCount: result.data.rowsSynced || 0 });
        setCurrentStep(4);
      } else { throw new Error(result.data?.error || 'BL sync failed'); }
    } catch (err) { setBlError(err.message); }
    finally { setBlSyncing(false); }
  };

  const syncLAP = async () => {
    if (!lapConfig) { setLapError('LAP sync not configured. Contact admin.'); return; }
    setLapSyncing(true); setLapError(null);
    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
      const result = await syncFunction({
        sheetId: lapConfig.sheetId, tabName: lapConfig.tabName,
        tableName: lapConfig.tableName, configId: lapConfig.id
      });
      if (result.data?.success) {
        setLapResult({ success: true, rowCount: result.data.rowsSynced || 0 });
        setCurrentStep(5);
      } else { throw new Error(result.data?.error || 'LAP sync failed'); }
    } catch (err) { setLapError(err.message); }
    finally { setLapSyncing(false); }
  };

  const syncContainerLog = async () => {
    if (!containerConfig) { setContainerError('Container Log sync not configured. Contact admin.'); return; }
    setContainerSyncing(true); setContainerError(null);
    try {
      const functions = getFunctions();
      const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
      const result = await syncFunction({
        sheetId: containerConfig.sheetId, tabName: containerConfig.tabName,
        tableName: containerConfig.tableName, configId: containerConfig.id
      });
      if (result.data?.success) {
        setContainerResult({ success: true, rowCount: result.data.rowsSynced || 0 });
      } else { throw new Error(result.data?.error || 'Container Log sync failed'); }
    } catch (err) { setContainerError(err.message); }
    finally { setContainerSyncing(false); }
  };

  const checkWalmartStatus = async () => {
    setWalmartLoading(true); setWalmartError(null);
    try {
      const functions = getFunctions();
      const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
      const result = await checkExternalTables({
        tables: [
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_bwi_csv' },
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_dsdc_csv' },
          { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_img_csv' }
        ]
      });
      setWalmartStatus(result.data);
      setCurrentStep(6);
    } catch (err) { setWalmartError(err.message); }
    finally { setWalmartLoading(false); }
  };

  const checkTargetStatus = async () => {
    setTargetLoading(true); setTargetError(null);
    try {
      const functions = getFunctions();
      const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
      const result = await checkExternalTables({
        tables: [{ project: 'actualizer2', dataset: 'inmocean_raw', table: 'target_csv' }]
      });
      setTargetStatus(result.data);
      setCurrentStep(7);
    } catch (err) { setTargetError(err.message); }
    finally { setTargetLoading(false); }
  };

  const runTransform = async () => {
    setTransforming(true); setTransformResult(null); setTransformError(null);
    try {
      const functions = getFunctions();
      const runOrderFileTransform = httpsCallable(functions, 'runOrderFileTransform', { timeout: 570000 });
      const result = await runOrderFileTransform({ suffix: '_csv' });
      setTransformResult(result.data);
      setCurrentStep(8);
    } catch (err) { setTransformError(err.message); }
    finally { setTransforming(false); }
  };

  const resetWorkflow = () => {
    setCurrentStep(1);
    setRabiFile(null); setRabiResult(null); setRabiError(null);
    setInvocFile(null); setInvocResult(null); setInvocError(null);
    setHistFile(null); setHistResult(null); setHistError(null);
    setErrorsFile(null); setErrorsResult(null); setErrorsError(null);
    setWipFile(null); setWipResult(null); setWipError(null);
    setRcptsFile(null); setRcptsResult(null); setRcptsError(null);
    setRcptsHistFile(null); setRcptsHistResult(null); setRcptsHistError(null);
    setInvFile(null); setInvResult(null); setInvError(null);
    setHtsFile(null); setHtsResult(null); setHtsError(null);
    setBlResult(null); setBlError(null);
    setLapResult(null); setLapError(null);
    setContainerResult(null); setContainerError(null);
    setWalmartStatus(null); setWalmartError(null);
    setTargetStatus(null); setTargetError(null);
    setTransformResult(null); setTransformError(null);
    setRunAllError(null);

    // CHA-88 Phase A — clear parallel-write upload state
    setOpenptFile(null);
    setOpenptUploading(false);
    setOpenptProgress(0);
    setOpenptResult(null);
    setOpenptError(null);

    setWalmartBwiFile(null);
    setWalmartBwiUploading(false);
    setWalmartBwiProgress(0);
    setWalmartBwiResult(null);
    setWalmartBwiError(null);

    setWalmartDsdcFile(null);
    setWalmartDsdcUploading(false);
    setWalmartDsdcProgress(0);
    setWalmartDsdcResult(null);
    setWalmartDsdcError(null);

    setWalmartImgFile(null);
    setWalmartImgUploading(false);
    setWalmartImgProgress(0);
    setWalmartImgResult(null);
    setWalmartImgError(null);

    setTargetUploadFile(null);
    setTargetUploadUploading(false);
    setTargetUploadProgress(0);
    setTargetUploadResult(null);
    setTargetUploadError(null);
  };

  // Run All Steps 3-7 in sequence
  const runAllSteps = async () => {
    setRunningAll(true); setRunAllError(null);
    try {
      const functions = getFunctions();

      if (blConfig) {
        setRunAllStep('BL Sync (Step 3)');
        setBlSyncing(true); setBlError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({ sheetId: blConfig.sheetId, tabName: blConfig.tabName, tableName: blConfig.tableName, configId: blConfig.id });
          if (result.data?.success) setBlResult({ success: true, rowCount: result.data.rowsSynced || 0 });
        } catch (err) { setBlError(err.message); }
        finally { setBlSyncing(false); }
      }

      if (lapConfig) {
        setRunAllStep('LAP Sync (Step 4)');
        setLapSyncing(true); setLapError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({ sheetId: lapConfig.sheetId, tabName: lapConfig.tabName, tableName: lapConfig.tableName, configId: lapConfig.id });
          if (result.data?.success) setLapResult({ success: true, rowCount: result.data.rowsSynced || 0 });
        } catch (err) { setLapError(err.message); }
        finally { setLapSyncing(false); }
      }

      if (containerConfig) {
        setRunAllStep('Container Log Sync');
        setContainerSyncing(true); setContainerError(null);
        try {
          const syncFunction = httpsCallable(functions, 'syncGoogleSheetsManual', { timeout: 330000 });
          const result = await syncFunction({ sheetId: containerConfig.sheetId, tabName: containerConfig.tabName, tableName: containerConfig.tableName, configId: containerConfig.id });
          if (result.data?.success) setContainerResult({ success: true, rowCount: result.data.rowsSynced || 0 });
        } catch (err) { setContainerError(err.message); }
        finally { setContainerSyncing(false); }
      }

      setRunAllStep('Walmart Check (Step 5)');
      setWalmartLoading(true); setWalmartError(null);
      try {
        const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
        const result = await checkExternalTables({
          tables: [
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_bwi_csv' },
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_dsdc_csv' },
            { project: 'actualizer2', dataset: 'inmocean_raw', table: 'walmart_tms_img_csv' }
          ]
        });
        setWalmartStatus(result.data);
      } catch (err) { setWalmartError(err.message); }
      finally { setWalmartLoading(false); }

      setRunAllStep('Target Check (Step 6)');
      setTargetLoading(true); setTargetError(null);
      try {
        const checkExternalTables = httpsCallable(functions, 'checkExternalTableStatus', { timeout: 60000 });
        const result = await checkExternalTables({
          tables: [{ project: 'actualizer2', dataset: 'inmocean_raw', table: 'target_csv' }]
        });
        setTargetStatus(result.data);
      } catch (err) { setTargetError(err.message); }
      finally { setTargetLoading(false); }

      setRunAllStep('Transform (Step 7)');
      setTransforming(true); setTransformResult(null); setTransformError(null);
      try {
        const runOrderFileTransform = httpsCallable(functions, 'runOrderFileTransform', { timeout: 570000 });
        const result = await runOrderFileTransform({ suffix: '_csv' });
        setTransformResult(result.data);
        setCurrentStep(8);
      } catch (err) { setTransformError(err.message); }
      finally { setTransforming(false); }

      setRunAllStep('Complete!');
    } catch (err) { setRunAllError(err.message); }
    finally { setRunningAll(false); setRunAllStep(null); }
  };

  // All production sub-steps done?
  const prodAllDone = !!errorsResult && !!wipResult && !!rcptsResult;

  const StepIndicator = ({ step, title, isActive, isCompleted }) => (
    <div className={`flex flex-col items-center gap-1 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isCompleted ? 'bg-green-500 text-white' :
          isActive ? 'bg-amber-600 text-white' :
            'bg-gray-200 text-gray-600'
        }`}>
        {isCompleted ? <CheckCircle className="w-4 h-4" /> : step}
      </div>
      <span className={`text-xs font-medium text-center ${isActive ? 'text-amber-700' : 'text-gray-500'}`}>{title}</span>
    </div>
  );

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const InfoPopup = ({ tableName, onClose }) => {
    const status = tableStatuses[tableName];
    return (
      <div className="absolute z-50 top-0 right-0 mt-8 mr-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 text-xs">
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-gray-800">Table Info</span>
          <button onClick={onClose} className="p-0.5 hover:bg-gray-100 rounded">
            <X className="w-3 h-3 text-gray-500" />
          </button>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <span className="text-gray-500">Table:</span>
            <span className="font-mono text-gray-800">{tableName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Dataset:</span>
            <span className="font-mono text-gray-800">inmocean_raw</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Last Updated:</span>
            <span className="text-gray-800">{status ? formatTimestamp(status.last_updated) : 'Never'}</span>
          </div>
          {status?.row_count !== undefined && (
            <div className="flex justify-between">
              <span className="text-gray-500">Rows:</span>
              <span className="text-gray-800">{status.row_count?.toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Compact card for grid layout
  const CompactCard = ({ stepNum, title, icon: Icon, isActive, isCompleted, children, borderColor = 'gray', tableName }) => (
    <div className={`bg-white rounded-lg border-2 ${isCompleted ? 'border-green-300 bg-green-50' :
        isActive ? `border-${borderColor}-400 ring-2 ring-${borderColor}-100` :
          'border-gray-200'
      } p-3 h-full relative`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isCompleted ? 'bg-green-500 text-white' :
            isActive ? 'bg-amber-600 text-white' :
              'bg-gray-200 text-gray-600'
          }`}>
          {isCompleted ? <CheckCircle className="w-3 h-3" /> : stepNum}
        </div>
        <Icon className={`h-4 w-4 ${isCompleted ? 'text-green-600' : isActive ? 'text-amber-600' : 'text-gray-400'}`} />
        <span className="text-sm font-medium text-gray-800 truncate flex-1">{title}</span>
        {tableName && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveInfoPopup(activeInfoPopup === tableName ? null : tableName);
            }}
            className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            title="View table info"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {activeInfoPopup === tableName && (
        <InfoPopup tableName={tableName} onClose={() => setActiveInfoPopup(null)} />
      )}
      <div className="text-xs">
        {children}
      </div>
    </div>
  );

  // CSV file upload card (reusable for each production sub-step)
  const CSVUploadSlot = ({ file, uploading, progress, result, error, onFileSelect, onUpload, inputId }) => (
    !result ? (
      <div className="space-y-2">
        <input type="file" accept=".csv" onChange={onFileSelect} className="hidden" id={inputId} />
        <label htmlFor={inputId} className="flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 cursor-pointer border border-amber-200">
          <Upload className="h-3 w-3" /> {file ? file.name.substring(0, 12) + '...' : 'Select CSV'}
        </label>
        {file && (
          <button onClick={onUpload} disabled={uploading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50">
            {uploading ? <><RefreshCw className="h-3 w-3 animate-spin" /> {progress}%</> : <><Play className="h-3 w-3" /> Upload</>}
          </button>
        )}
        {error && <div className="text-red-500 truncate">{error}</div>}
      </div>
    ) : (
      <div className="text-green-600">{result.rowCount?.toLocaleString()} rows</div>
    )
  );

  return (
    <div className="space-y-5">
      {/* Header - Amber/Orange gradient */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Zap className="h-7 w-7 text-amber-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Power BI Beta - CSV Upload Workflow</h1>
              <p className="text-gray-600 text-sm">CSV upload testing - data writes to _csv tables for comparison</p>
            </div>
          </div>
          <button
            onClick={runAllSteps}
            disabled={runningAll || (!blConfig && !lapConfig)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-md"
          >
            {runningAll ? (
              <><RefreshCw className="h-4 w-4 animate-spin" />{runAllStep || 'Running...'}</>
            ) : (
              <><Play className="h-4 w-4" />Run All (3-7)</>
            )}
          </button>
        </div>
        {runAllError && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
            <XCircle className="h-4 w-4" />{runAllError}
          </div>
        )}
      </div>

      {/* Upload Mode Toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-600">Upload Mode:</span>
        <div className="flex bg-slate-100 rounded-full p-0.5">
          <button onClick={() => setUploadMode('system')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${uploadMode === 'system' ? 'bg-amber-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
            System (Azure)
          </button>
          <button onClick={() => setUploadMode('manual')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${uploadMode === 'manual' ? 'bg-amber-600 text-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
            Manual Upload
          </button>
        </div>
        {uploadMode === 'system' && (
          <button onClick={() => triggerSync(true)} disabled={syncing}
            className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 text-sm text-amber-700 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Refresh All'}
          </button>
        )}
      </div>

      {/* System Sync Status */}
      {uploadMode === 'system' && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Azure Data Lake Sync</h3>
            {syncing && (
              <span className="text-xs text-amber-600 flex items-center gap-1.5">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Syncing {syncingFile ? `(${syncingFile})` : ''}...
              </span>
            )}
          </div>

          {/* Progress bar during sync */}
          {syncing && (
            <div className="mb-4">
              <div className="flex gap-1">
                {POWERBI_KEYS.map(k => {
                  const s = syncProgress[k];
                  const bg = s === 'success' ? 'bg-green-500' : s === 'skipped' ? 'bg-blue-400' : s === 'syncing' ? 'bg-amber-500 animate-pulse' : s === 'error' ? 'bg-red-500' : 'bg-slate-200';
                  return <div key={k} className={`h-2 flex-1 rounded-full ${bg} transition-all duration-300`} title={k} />;
                })}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">{Object.values(syncProgress).filter(s => s === 'success' || s === 'skipped').length} / {POWERBI_KEYS.length}</span>
                <div className="flex gap-3 text-[10px]">
                  <span className="text-green-600">done</span>
                  <span className="text-blue-500">skipped</span>
                  <span className="text-amber-500">syncing</span>
                  <span className="text-slate-400">pending</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            {(syncStatus || POWERBI_KEYS.map(k => ({ key: k, label: k, step: '', status: 'never_synced' }))).map(f => {
              const liveStatus = syncProgress[f.key];
              const statusColor = liveStatus === 'syncing' ? 'border-amber-300 bg-amber-50' :
                liveStatus === 'success' ? 'border-green-300 bg-green-50' :
                liveStatus === 'error' ? 'border-red-300 bg-red-50' :
                'border-slate-100 bg-slate-50';
              return (
                <div key={f.key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${statusColor} transition-all`}>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded min-w-[28px] text-center">{f.step}</span>
                    <div>
                      <span className="text-sm font-medium text-slate-700">{f.label}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{f.tableName}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {f.rowCount > 0 && <span className="text-xs font-medium text-slate-600">{Number(f.rowCount).toLocaleString()} rows</span>}
                    {f.azureLastModified && (
                      <span className="text-[10px] text-slate-400" title="Azure file timestamp">
                        File: {new Date(f.azureLastModified).toLocaleString()}
                      </span>
                    )}
                    {f.lastSync ? (
                      <span className="text-xs text-green-600" title="Last synced">
                        Synced: {new Date(f.lastSync).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Never synced</span>
                    )}
                    {liveStatus === 'syncing' ? (
                      <RefreshCw className="h-3.5 w-3.5 text-amber-500 animate-spin" />
                    ) : liveStatus === 'success' ? (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                    ) : liveStatus === 'skipped' ? (
                      <span className="text-[10px] text-blue-500 font-medium">unchanged</span>
                    ) : liveStatus === 'error' ? (
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                    ) : (
                      <span className={`w-2.5 h-2.5 rounded-full ${f.status === 'success' ? 'bg-green-500' : f.status === 'error' ? 'bg-red-500' : 'bg-slate-300'}`} />
                    )}
                    <button onClick={(e) => { e.stopPropagation(); syncSingleFile(f.key); }} disabled={syncing}
                      className="px-2 py-0.5 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 disabled:opacity-30"
                      title={`Refresh ${f.label}`}>
                      Sync
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Steps 3-7 Status */}
          {transformStatus && (
            <div className={`mt-3 flex items-center justify-between px-3 py-2.5 rounded-lg border ${
              transformStatus.status === 'running' ? 'border-purple-300 bg-purple-50' :
              transformStatus.status === 'success' ? 'border-green-300 bg-green-50' :
              transformStatus.status === 'error' ? 'border-red-300 bg-red-50' :
              'border-slate-100 bg-slate-50'
            }`}>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">3-7</span>
                <div>
                  <span className="text-sm font-medium text-slate-700">BL + LAP + Container Log + Walmart/Target Check + Transform</span>
                  <span className="text-[10px] text-slate-400 block">Runs automatically after file sync</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {transformStatus.status === 'running' && (
                  <><RefreshCw className="h-3.5 w-3.5 text-purple-500 animate-spin" /><span className="text-xs text-purple-600">Running...</span></>
                )}
                {transformStatus.status === 'success' && (
                  <><CheckCircle className="h-3.5 w-3.5 text-green-500" /><span className="text-xs text-green-600">{new Date(transformStatus.completedAt).toLocaleString()}</span></>
                )}
                {transformStatus.status === 'error' && (
                  <><XCircle className="h-3.5 w-3.5 text-red-500" /><span className="text-xs text-red-600">{transformStatus.lastError?.slice(0, 80)}</span></>
                )}
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400 mt-3">Auto-syncs Mon-Fri 8:10am, 9:10am, 10:10am, 12:10pm, 2:10pm ET. Files only re-upload when changed in Azure.</p>
        </div>
      )}

      {uploadMode === 'manual' && <>
      {/* Progress Steps - Grouped */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
        <div className="flex items-center gap-4 min-w-max">
          {/* ERP Data Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-amber-600 mb-2 uppercase tracking-wide">ERP Data (CSV)</span>
            <div className="flex items-center gap-1 bg-amber-50 rounded-lg px-2 py-1 border border-amber-200">
              <StepIndicator step={1} title="Orders" isActive={currentStep === 1} isCompleted={!!rabiResult} />
              <ChevronRight className="w-3 h-3 text-amber-300 flex-shrink-0" />
              <StepIndicator step={2} title="Prod" isActive={currentStep === 2} isCompleted={prodAllDone} />
              <ChevronRight className="w-3 h-3 text-amber-300 flex-shrink-0" />
              <StepIndicator step="2.1" title="Inv" isActive={currentStep === 2.1} isCompleted={!!invResult} />
              <ChevronRight className="w-3 h-3 text-amber-300 flex-shrink-0" />
              <StepIndicator step="2.2" title="HTS" isActive={currentStep === 2.2} isCompleted={!!htsResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Warehouse Data Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-green-600 mb-2 uppercase tracking-wide">Warehouse</span>
            <div className="flex items-center gap-1 bg-green-50 rounded-lg px-2 py-1 border border-green-200">
              <StepIndicator step={3} title="BL" isActive={currentStep === 3} isCompleted={!!blResult} />
              <ChevronRight className="w-3 h-3 text-green-300 flex-shrink-0" />
              <StepIndicator step={4} title="LAP" isActive={currentStep === 4} isCompleted={!!lapResult} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Customer TMS Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-orange-600 mb-2 uppercase tracking-wide">Customer TMS</span>
            <div className="flex items-center gap-1 bg-orange-50 rounded-lg px-2 py-1 border border-orange-200">
              <StepIndicator step={5} title="Walmart" isActive={currentStep === 5} isCompleted={!!walmartStatus} />
              <ChevronRight className="w-3 h-3 text-orange-300 flex-shrink-0" />
              <StepIndicator step={6} title="Target" isActive={currentStep === 6} isCompleted={!!targetStatus} />
            </div>
          </div>

          <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

          {/* Transform Group */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-purple-600 mb-2 uppercase tracking-wide">Transform</span>
            <div className="flex items-center gap-1 bg-purple-50 rounded-lg px-2 py-1 border border-purple-200">
              <StepIndicator step={7} title="Run" isActive={currentStep === 7} isCompleted={!!transformResult} />
              <ChevronRight className="w-3 h-3 text-purple-300 flex-shrink-0" />
              <StepIndicator step={8} title="Done" isActive={currentStep === 8} isCompleted={false} />
            </div>
          </div>
        </div>
      </div>

      {/* ========== ERP DATA SECTION (CSV) ========== */}
      <div className="flex items-center gap-3 mt-2">
        <div className="h-px flex-1 bg-amber-200"></div>
        <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-2">
          <Database className="h-4 w-4" /> ERP Data (CSV)
        </span>
        <div className="h-px flex-1 bg-amber-200"></div>
      </div>

      {/* Row 1: Step 1, 1.1, 1.2 - Orders CSV (Pick+Open, Current Invoices, Historical Invoices — all REPLACE) */}
      <div className="grid grid-cols-4 gap-3">
        <CompactCard stepNum={1} title="Pick & Open CSV" icon={FileText} isActive={currentStep === 1} isCompleted={!!rabiResult} borderColor="amber" tableName="rabi_invoices_v2_csv_po">
          <CSVUploadSlot
            file={rabiFile} uploading={rabiUploading} progress={rabiProgress}
            result={rabiResult} error={rabiError}
            onFileSelect={handleRabiFileSelect} onUpload={uploadRabiInvoice}
            inputId="beta-rabi-file-input"
          />
          <div className="text-[10px] text-amber-500 mt-1">REPLACE — replaces all P+O data each upload</div>
        </CompactCard>
        <CompactCard stepNum="1.1" title="Current Invoices" icon={FileText} isActive={currentStep === 1} isCompleted={!!invocResult} borderColor="amber" tableName="rabi_invoices_v2_csv_inv">
          <CSVUploadSlot
            file={invocFile} uploading={invocUploading} progress={invocProgress}
            result={invocResult} error={invocError}
            onFileSelect={handleInvocFileSelect} onUpload={uploadInvoices}
            inputId="beta-invoc-file-input"
          />
          <div className="text-[10px] text-amber-500 mt-1">REPLACE — current invoice data</div>
        </CompactCard>
        <CompactCard stepNum="1.2" title="Historical Invoices" icon={FileText} isActive={currentStep === 1} isCompleted={!!histResult} borderColor="amber" tableName="rabi_invoices_v2_csv_inv_hist">
          <CSVUploadSlot
            file={histFile} uploading={histUploading} progress={histProgress}
            result={histResult} error={histError}
            onFileSelect={handleHistFileSelect} onUpload={uploadHistorical}
            inputId="beta-hist-file-input"
          />
          <div className="text-[10px] text-amber-500 mt-1">REPLACE — historical invoice data</div>
        </CompactCard>
        <div className="border-2 border-dashed border-amber-100 rounded-lg p-3 flex items-center justify-center text-amber-300 text-xs">
          CSV uploads write to _csv suffixed tables
        </div>
      </div>

      {/* Row 2: Step 2a, 2b, 2c - Production CSVs (3 columns) */}
      <div className="grid grid-cols-3 gap-3">
        <CompactCard stepNum="2a" title="Errors CSV" icon={AlertCircle} isActive={currentStep === 2} isCompleted={!!errorsResult} borderColor="amber" tableName="production_errors_csv">
          <CSVUploadSlot
            file={errorsFile} uploading={errorsUploading} progress={errorsProgress}
            result={errorsResult} error={errorsError}
            onFileSelect={handleErrorsFileSelect} onUpload={uploadErrorsData}
            inputId="beta-errors-file-input"
          />
        </CompactCard>

        <CompactCard stepNum="2b" title="OpenWIP CSV" icon={Database} isActive={currentStep === 2} isCompleted={!!wipResult} borderColor="amber" tableName="production_open_wip_csv">
          <CSVUploadSlot
            file={wipFile} uploading={wipUploading} progress={wipProgress}
            result={wipResult} error={wipError}
            onFileSelect={handleWipFileSelect} onUpload={uploadWipData}
            inputId="beta-wip-file-input"
          />
        </CompactCard>

        <CompactCard stepNum="2c" title="2025 Receipts CSV" icon={FileSpreadsheet} isActive={currentStep === 2} isCompleted={!!rcptsResult} borderColor="amber" tableName="production_2025_receipts_csv">
          <CSVUploadSlot
            file={rcptsFile} uploading={rcptsUploading} progress={rcptsProgress}
            result={rcptsResult} error={rcptsError}
            onFileSelect={handleRcptsFileSelect} onUpload={uploadRcptsData}
            inputId="beta-rcpts-file-input"
          />
        </CompactCard>

        <CompactCard stepNum="2d" title="2025 Receipts Historical" icon={FileSpreadsheet} isActive={currentStep === 2} isCompleted={!!rcptsHistResult} borderColor="amber" tableName="production_2025_receipts_csv_hist">
          <CSVUploadSlot
            file={rcptsHistFile} uploading={rcptsHistUploading} progress={rcptsHistProgress}
            result={rcptsHistResult} error={rcptsHistError}
            onFileSelect={handleRcptsHistFileSelect} onUpload={uploadRcptsHistData}
            inputId="beta-rcpts-hist-file-input"
          />
        </CompactCard>
      </div>

      {/* Row 3: Step 2.1 (Inventory) + Step 2.2 (HTS) */}
      <div className="grid grid-cols-2 gap-3">
        <CompactCard stepNum="2.1" title="Inventory CSV" icon={Boxes} isActive={currentStep === 2.1} isCompleted={!!invResult} borderColor="amber" tableName="inventory_csv">
          <CSVUploadSlot
            file={invFile} uploading={invUploading} progress={invProgress}
            result={invResult} error={invError}
            onFileSelect={handleInvFileSelect} onUpload={uploadInventoryData}
            inputId="beta-inv-file-input"
          />
        </CompactCard>

        <CompactCard stepNum="2.2" title="HTS Data CSV" icon={FileText} isActive={currentStep === 2.2} isCompleted={!!htsResult} borderColor="amber" tableName="hts_updates_csv">
          <CSVUploadSlot
            file={htsFile} uploading={htsUploading} progress={htsProgress}
            result={htsResult} error={htsError}
            onFileSelect={handleHtsFileSelect} onUpload={uploadHtsData}
            inputId="beta-hts-file-input"
          />
        </CompactCard>
      </div>

      {/* ========== 🆕 CHA-88 PHASE A — OPEN PT UPLOAD ========== */}
      <div className="flex items-center gap-3 mt-2">
        <div className="h-px flex-1 bg-amber-200"></div>
        <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-2">
          <Truck className="h-4 w-4" /> Open PT — Phase A Parallel Write
        </span>
        <div className="h-px flex-1 bg-amber-200"></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <CompactCard stepNum="2.3" title="Open PT CSV" icon={Truck} isActive={false} isCompleted={!!openptResult} borderColor="amber" tableName="openpt_csv">
          <CSVUploadSlot
            file={openptFile} uploading={openptUploading} progress={openptProgress}
            result={openptResult} error={openptError}
            onFileSelect={handleOpenptFileSelect} onUpload={uploadOpenpt}
            inputId="beta-openpt-file-input"
          />
          <div className="text-[10px] text-amber-500 mt-1">REPLACE — Open PT data</div>
        </CompactCard>
        <div className="border-2 border-dashed border-amber-100 rounded-lg p-3 flex items-center justify-center text-amber-300 text-xs col-span-3">
          CHA-88 — InMocean upload slots
        </div>
      </div>

      {/* ========== WAREHOUSE DATA SECTION (same as original) ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-green-200"></div>
        <span className="text-sm font-semibold text-green-600 uppercase tracking-wide flex items-center gap-2">
          <Boxes className="h-4 w-4" /> Warehouse Data
        </span>
        <div className="h-px flex-1 bg-green-200"></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <CompactCard stepNum={3} title="BL Routing" icon={Navigation} isActive={currentStep === 3} isCompleted={!!blResult} borderColor="green" tableName="bl">
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z" />
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z" />
              <path fill="#4285f4" d="M13 3v6h6l-6-6z" />
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!blResult ? (
            <div className="space-y-2">
              {blConfig ? (
                <button onClick={syncBL} disabled={blSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {blSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {blError && <div className="text-red-500 truncate">{blError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{blResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        <CompactCard stepNum={4} title="LAP Routing" icon={Navigation} isActive={currentStep === 4} isCompleted={!!lapResult} borderColor="green" tableName="lap">
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z" />
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z" />
              <path fill="#4285f4" d="M13 3v6h6l-6-6z" />
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!lapResult ? (
            <div className="space-y-2">
              {lapConfig ? (
                <button onClick={syncLAP} disabled={lapSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {lapSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {lapError && <div className="text-red-500 truncate">{lapError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{lapResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        <CompactCard stepNum="" title="Container Log" icon={Boxes} isActive={false} isCompleted={!!containerResult} borderColor="green" tableName="container_log">
          <div className="flex items-center gap-1 mb-1">
            <svg viewBox="0 0 24 24" className="h-3 w-3">
              <path fill="#34a853" d="M19 11V9h-6V3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4h-8v-4h8z" />
              <path fill="#188038" d="M19 9v2h-6v4h-2V9h8z" />
              <path fill="#4285f4" d="M13 3v6h6l-6-6z" />
            </svg>
            <span className="text-[10px] text-gray-400">Google Sheets</span>
          </div>
          {!containerResult ? (
            <div className="space-y-2">
              {containerConfig ? (
                <button onClick={syncContainerLog} disabled={containerSyncing} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                  {containerSyncing ? <><RefreshCw className="h-3 w-3 animate-spin" /> Sync</> : <><RefreshCw className="h-3 w-3" /> Refresh</>}
                </button>
              ) : (
                <div className="text-amber-600">Not configured</div>
              )}
              {containerError && <div className="text-red-500 truncate">{containerError}</div>}
            </div>
          ) : (
            <div className="text-green-600">{containerResult.rowCount?.toLocaleString()} rows</div>
          )}
        </CompactCard>

        <div className="border-2 border-dashed border-green-200 rounded-lg p-3 flex items-center justify-center text-green-400 text-xs">
          + More
        </div>
      </div>

      {/* ========== CUSTOMER TMS SECTION (same as original) ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-orange-200"></div>
        <span className="text-sm font-semibold text-orange-600 uppercase tracking-wide flex items-center gap-2">
          <Truck className="h-4 w-4" /> Customer TMS
        </span>
        <div className="h-px flex-1 bg-orange-200"></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {/* Walmart */}
        <div className={`bg-white rounded-lg border-2 ${walmartStatus ? 'border-green-300 bg-green-50' :
            currentStep === 5 ? 'border-blue-400 ring-2 ring-blue-100' :
              'border-gray-200'
          } p-3 h-full relative`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${walmartStatus ? 'bg-green-500 text-white' :
                currentStep === 5 ? 'bg-amber-600 text-white' :
                  'bg-gray-200 text-gray-600'
              }`}>
              {walmartStatus ? <CheckCircle className="w-3 h-3" /> : 5}
            </div>
            <svg viewBox="0 0 32 32" className="h-5 w-5">
              <g fill="#ffc220">
                <path d="M16 3 L17.5 11 L16 14 L14.5 11 Z" />
                <path d="M16 29 L17.5 21 L16 18 L14.5 21 Z" />
                <path d="M3 16 L11 14.5 L14 16 L11 17.5 Z" />
                <path d="M29 16 L21 14.5 L18 16 L21 17.5 Z" />
                <path d="M6.5 6.5 L12 10.5 L13.5 13.5 L10.5 12 Z" />
                <path d="M25.5 25.5 L20 21.5 L18.5 18.5 L21.5 20 Z" />
                <path d="M25.5 6.5 L21.5 12 L18.5 13.5 L20 10.5 Z" />
                <path d="M6.5 25.5 L10.5 20 L13.5 18.5 L12 21.5 Z" />
              </g>
            </svg>
            <span className="text-sm font-bold text-[#0071ce] flex-1">Walmart</span>
          </div>
          <div className="text-xs">
            <div className="flex items-center gap-1 mb-1">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#6b7280" strokeWidth="2">
                <ellipse cx="12" cy="6" rx="8" ry="3" />
                <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
                <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
              </svg>
              <span className="text-[10px] text-gray-400">Database</span>
            </div>
            {!walmartStatus ? (
              <div className="space-y-2">
                <div className="text-gray-500 truncate">3 TMS tables</div>
                <button onClick={checkWalmartStatus} disabled={walmartLoading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-[#0071ce] text-white rounded hover:bg-[#004c91] disabled:opacity-50">
                  {walmartLoading ? <><RefreshCw className="h-3 w-3 animate-spin" /></> : <><Database className="h-3 w-3" /> Check</>}
                </button>
                {walmartError && <div className="text-red-500 truncate">{walmartError}</div>}
              </div>
            ) : (
              <div className="text-green-600">
                {walmartStatus.tables?.reduce((sum, t) => sum + (t.rowCount || 0), 0).toLocaleString()} rows
              </div>
            )}
          </div>
        </div>

        {/* Target */}
        <div className={`bg-white rounded-lg border-2 ${targetStatus ? 'border-green-300 bg-green-50' :
            currentStep === 6 ? 'border-red-400 ring-2 ring-red-100' :
              'border-gray-200'
          } p-3 h-full relative`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${targetStatus ? 'bg-green-500 text-white' :
                currentStep === 6 ? 'bg-amber-600 text-white' :
                  'bg-gray-200 text-gray-600'
              }`}>
              {targetStatus ? <CheckCircle className="w-3 h-3" /> : 6}
            </div>
            <svg viewBox="0 0 1615 1551" className="h-5 w-5">
              <circle cx="808" cy="775" r="775" fill="#cc0000" />
              <circle cx="808" cy="775" r="530" fill="#fff" />
              <circle cx="808" cy="775" r="285" fill="#cc0000" />
            </svg>
            <span className="text-sm font-medium text-gray-800 flex-1">Target</span>
          </div>
          <div className="text-xs">
            <div className="flex items-center gap-1 mb-1">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="#6b7280" strokeWidth="2">
                <ellipse cx="12" cy="6" rx="8" ry="3" />
                <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
                <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
              </svg>
              <span className="text-[10px] text-gray-400">Database</span>
            </div>
            {!targetStatus ? (
              <div className="space-y-2">
                <div className="text-gray-500 truncate">Target TMS</div>
                <button onClick={checkTargetStatus} disabled={targetLoading} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-[#cc0000] text-white rounded hover:bg-[#990000] disabled:opacity-50">
                  {targetLoading ? <><RefreshCw className="h-3 w-3 animate-spin" /></> : <><Database className="h-3 w-3" /> Check</>}
                </button>
                {targetError && <div className="text-red-500 truncate">{targetError}</div>}
              </div>
            ) : (
              <div className="text-green-600">{targetStatus.tables?.[0]?.rowCount?.toLocaleString()} rows</div>
            )}
          </div>
        </div>

        <div className="border-2 border-dashed border-orange-200 rounded-lg p-3 flex items-center justify-center text-orange-400 text-xs">
          + More
        </div>
        <div className="border-2 border-dashed border-orange-200 rounded-lg p-3 flex items-center justify-center text-orange-400 text-xs">
          + More
        </div>
      </div>

      {/* ========== 🆕 CHA-88 PHASE A — CUSTOMER TMS UPLOADS ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-orange-200"></div>
        <span className="text-sm font-semibold text-orange-600 uppercase tracking-wide flex items-center gap-2">
          <Upload className="h-4 w-4" /> Customer TMS Uploads — Phase A Parallel Write
        </span>
        <div className="h-px flex-1 bg-orange-200"></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <CompactCard stepNum="" title="Walmart BWI" icon={Truck} isActive={false} isCompleted={!!walmartBwiResult} borderColor="orange" tableName="walmart_tms_bwi_csv">
          <CSVUploadSlot
            file={walmartBwiFile} uploading={walmartBwiUploading} progress={walmartBwiProgress}
            result={walmartBwiResult} error={walmartBwiError}
            onFileSelect={handleWalmartBwiFileSelect} onUpload={uploadWalmartBwi}
            inputId="beta-walmart-bwi-file-input"
          />
          <div className="text-[10px] text-orange-500 mt-1">REPLACE — Walmart BWI TMS data</div>
        </CompactCard>

        <CompactCard stepNum="" title="Walmart DSDC" icon={Truck} isActive={false} isCompleted={!!walmartDsdcResult} borderColor="orange" tableName="walmart_tms_dsdc_csv">
          <CSVUploadSlot
            file={walmartDsdcFile} uploading={walmartDsdcUploading} progress={walmartDsdcProgress}
            result={walmartDsdcResult} error={walmartDsdcError}
            onFileSelect={handleWalmartDsdcFileSelect} onUpload={uploadWalmartDsdc}
            inputId="beta-walmart-dsdc-file-input"
          />
          <div className="text-[10px] text-orange-500 mt-1">REPLACE — Walmart DSDC TMS data</div>
        </CompactCard>

        <CompactCard stepNum="" title="Walmart IMG" icon={Truck} isActive={false} isCompleted={!!walmartImgResult} borderColor="orange" tableName="walmart_tms_img_csv">
          <CSVUploadSlot
            file={walmartImgFile} uploading={walmartImgUploading} progress={walmartImgProgress}
            result={walmartImgResult} error={walmartImgError}
            onFileSelect={handleWalmartImgFileSelect} onUpload={uploadWalmartImg}
            inputId="beta-walmart-img-file-input"
          />
          <div className="text-[10px] text-orange-500 mt-1">REPLACE — Walmart IMG TMS data</div>
        </CompactCard>

        <CompactCard stepNum="" title="Target" icon={Target} isActive={false} isCompleted={!!targetUploadResult} borderColor="orange" tableName="target_csv">
          <CSVUploadSlot
            file={targetUploadFile} uploading={targetUploadUploading} progress={targetUploadProgress}
            result={targetUploadResult} error={targetUploadError}
            onFileSelect={handleTargetUploadFileSelect} onUpload={uploadTargetCsv}
            inputId="beta-target-upload-file-input"
          />
          <div className="text-[10px] text-orange-500 mt-1">REPLACE — Target TMS data</div>
        </CompactCard>
      </div>

      {/* ========== DATA TRANSFORMATION SECTION (same as original) ========== */}
      <div className="flex items-center gap-3 mt-4">
        <div className="h-px flex-1 bg-purple-200"></div>
        <span className="text-sm font-semibold text-purple-600 uppercase tracking-wide flex items-center gap-2">
          <Zap className="h-4 w-4" /> Data Transformation
        </span>
        <div className="h-px flex-1 bg-purple-200"></div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {/* Step 7: Transform */}
        <div className={`bg-white rounded-lg border-2 ${transformResult ? 'border-green-300 bg-green-50' :
            currentStep === 7 ? 'border-purple-400 ring-2 ring-purple-100' :
              'border-gray-200'
          } p-3 h-full`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${transformResult ? 'bg-green-500 text-white' :
                currentStep === 7 ? 'bg-amber-600 text-white' :
                  'bg-gray-200 text-gray-600'
              }`}>
              {transformResult ? <CheckCircle className="w-3 h-3" /> : 7}
            </div>
            <Zap className={`h-4 w-4 ${transformResult ? 'text-green-600' : 'text-purple-600'}`} />
            <span className="text-sm font-medium text-gray-800">Transform</span>
          </div>
          <div className="text-xs">
            {!transformResult ? (
              <div className="space-y-2">
                <button onClick={runTransform} disabled={transforming} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
                  {transforming ? <><RefreshCw className="h-3 w-3 animate-spin" /> Running...</> : <><Zap className="h-3 w-3" /> Run</>}
                </button>
                {transformError && <div className="text-red-500 truncate">{transformError}</div>}
              </div>
            ) : (
              <div className="text-green-600">
                {(transformResult.results?.orderfile?.rows || 0) +
                  (transformResult.results?.production_open_wip?.rows || 0) +
                  (transformResult.results?.production_receipts?.rows || 0)} rows
              </div>
            )}
          </div>
        </div>

        {/* Step 8: Done */}
        <div className={`bg-white rounded-lg border-2 ${currentStep === 8 ? 'border-green-400 bg-green-50 ring-2 ring-green-100' :
            'border-gray-200'
          } p-3 h-full`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${currentStep === 8 ? 'bg-green-500 text-white' :
                'bg-gray-200 text-gray-600'
              }`}>
              {currentStep === 8 ? <CheckCircle className="w-3 h-3" /> : 8}
            </div>
            <CheckCircle className={`h-4 w-4 ${currentStep === 8 ? 'text-green-600' : 'text-gray-400'}`} />
            <span className="text-sm font-medium text-gray-800">Done</span>
          </div>
          <div className="text-xs">
            {currentStep === 8 ? (
              <div className="space-y-2">
                <div className="text-green-600 font-medium">Complete!</div>
                <button onClick={resetWorkflow} className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700">
                  <RefreshCw className="h-3 w-3" /> Reset
                </button>
              </div>
            ) : (
              <div className="text-gray-400">Waiting...</div>
            )}
          </div>
        </div>

        <div className="border-2 border-dashed border-purple-200 rounded-lg p-3 flex items-center justify-center text-purple-400 text-xs"></div>
        <div className="border-2 border-dashed border-purple-200 rounded-lg p-3 flex items-center justify-center text-purple-400 text-xs"></div>
      </div>
    </>}
    </div>
  );
}
